import type { FormEvent, KeyboardEvent, PointerEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import ModalHeader, { StyledModalHeader } from 'LuxKit/Modal/ModalHeader'
import LineArrowToBottomIcon from 'LuxKit/Icons/line/LineArrowToBottomIcon'
import LineEditIcon from 'LuxKit/Icons/line/LineEditIcon'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import TripNameField from 'tripPlanner/components/TripModal/Trips/TripDetailsFields/TripNameField'
import { useRenameChatMutation } from 'tripPlanner/hooks/api/chat'
import { useTripChatItineraryExport } from '../hooks/useTripChatItineraryExport'

const ACTIONS_SHEET_TOP_RADIUS = rem(32)
const CLOSE_DRAG_DISTANCE_PX = 64

const SheetHandleWrapper = styled.div`
  display: flex;
  grid-area: sheet-handle;
  justify-content: center;
  padding: ${rem(20)} ${rem(20)} ${rem(12)};
`

const SheetHandleButton = styled.button`
  display: flex;
  width: ${rem(72)};
  min-height: ${rem(24)};
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  cursor: pointer;
  touch-action: pan-y;

  &:disabled {
    cursor: default;
  }
`

const SheetHandle = styled.span`
  width: ${rem(48)};
  height: ${rem(4)};
  border-radius: ${rem(999)};
  background-color: ${props => props.theme.palette.neutral.surface.lightForeground};
`

const ActionsSheetModal = styled(ModalBase)`
  ${mediaQueryOnly.mobile} {
    &&&.mode-default.height-auto {
      border-start-start-radius: ${ACTIONS_SHEET_TOP_RADIUS};
      border-start-end-radius: ${ACTIONS_SHEET_TOP_RADIUS};
      border-end-end-radius: 0;
      border-end-start-radius: 0;
      overflow: hidden;
    }

    &&&.mode-default.height-auto ${StyledModalHeader} {
      border-start-start-radius: ${ACTIONS_SHEET_TOP_RADIUS};
      border-start-end-radius: ${ACTIONS_SHEET_TOP_RADIUS};
      border-end-end-radius: 0;
      border-end-start-radius: 0;
    }
  }

  ${mediaQueryUp.tablet} {
    &&&.mode-default.height-auto {
      border-radius: ${props => props.theme.borderRadius.L};
    }
  }

  &,
  > form {
    grid-template-areas:
      "sheet-handle"
      "modal-header"
      "modal-body"
      "modal-footer";
    grid-template-rows:
      min-content
      min-content
      1fr
      min-content;
  }
`

const RenameForm = styled.form``

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: env(safe-area-inset-bottom);
`

const ActionListItem = styled(ClickableListItem)`
  min-height: ${rem(64)};
  border-radius: 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &[aria-disabled="true"] {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    pointer-events: none;
  }
`

const ActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  color: currentColor;
`

const RenameFields = styled.div`
  display: grid;
  gap: ${rem(16)};
  padding-bottom: ${rem(16)};
`

interface Props {
  chatId: number | undefined
  onClose: () => void
  onRenameSuccess?: (name: string) => void
  persistedTitle?: string
  title: string
}

function TripChatMobileCurrentTripActionsSheet({
  chatId,
  onClose,
  onRenameSuccess,
  persistedTitle,
  title,
}: Props) {
  const intl = useIntl()
  const dragStartYRef = useRef<number | undefined>(undefined)
  const renameInFlightRef = useRef(false)
  const [displayTitle, setDisplayTitle] = useState(title)
  const [isRenameFormOpen, setIsRenameFormOpen] = useState(false)
  const [draftName, setDraftName] = useState(title)
  const [renameError, setRenameError] = useState<string | undefined>(undefined)
  const { mutateAsync: renameChat, isLoading: isRenameLoading } = useRenameChatMutation()

  useEffect(() => {
    setDisplayTitle(title)
    if (!isRenameFormOpen) {
      setDraftName(title)
    }
  }, [isRenameFormOpen, title])

  const {
    downloadPdf,
    downloadSpreadsheet,
    isExportLoading,
  } = useTripChatItineraryExport({
    chatId,
  })

  const downloadPdfLabel = intl.formatMessage({
    id: 'Ajs48ExuQZR6p1horez-',
    defaultMessage: 'Download PDF',
    description: 'Action sheet option label to download the current mobile TripPlanner itinerary as a PDF file',
  })
  const downloadSpreadsheetLabel = intl.formatMessage({
    id: 'ErULFhHLKCs5-eihYHfj',
    defaultMessage: 'Download spreadsheet',
    description: 'Action sheet option label to download the current mobile TripPlanner itinerary as a spreadsheet file',
  })
  const renameTripLabel = intl.formatMessage({
    id: 'hEqZyKwVvbSOPnkXnD9y',
    defaultMessage: 'Rename trip',
    description: 'Action sheet option label to rename the current mobile TripPlanner chat itinerary',
  })
  const renameInputLabel = intl.formatMessage({
    id: '9tHvvAg0BeLQWFPRg6ZF',
    defaultMessage: 'Trip name',
    description: 'Input label for renaming the current mobile TripPlanner chat itinerary',
  })
  const renameFailedError = intl.formatMessage({
    id: 'MglHeqduRY3LIHFdKCDr',
    defaultMessage: 'Unable to rename trip. Please try again.',
    description: 'Snackbar error shown when renaming the current mobile TripPlanner itinerary fails',
  })
  const emptyNameError = intl.formatMessage({
    id: 'PEpiT2pC31v16iLZ0wPa',
    defaultMessage: 'Name cannot be empty',
    description: 'Validation error shown when the mobile TripPlanner itinerary rename input is left empty',
  })
  const closeSheetHandleLabel = intl.formatMessage({
    id: 'Iind106KAQjFCOnGMAkP',
    defaultMessage: 'Close actions sheet',
    description: 'Accessible label for the mobile TripPlanner actions sheet drag handle close affordance',
  })
  const saveLabel = intl.formatMessage({
    id: 'OcE5Mss3nLR6ryLzGrWQ',
    defaultMessage: 'Save',
    description: 'Button label to save the renamed current mobile TripPlanner itinerary',
  })
  const cancelLabel = intl.formatMessage({
    id: 'zUiZ9LN6JGqM3nr1_lEh',
    defaultMessage: 'Cancel',
    description: 'Button label to cancel renaming the current mobile TripPlanner itinerary',
  })

  const openRenameForm = useCallback(() => {
    setDraftName(displayTitle)
    setRenameError(undefined)
    setIsRenameFormOpen(true)
  }, [displayTitle])

  const closeRenameForm = useCallback(() => {
    setDraftName(displayTitle)
    setRenameError(undefined)
    setIsRenameFormOpen(false)
  }, [displayTitle])

  const handleRenameChange = useCallback((nextName: string) => {
    setDraftName(nextName)
    setRenameError(undefined)
  }, [])

  const canDismiss = !isRenameLoading

  const handleHandlePointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (!canDismiss) return

    dragStartYRef.current = event.clientY
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [canDismiss])

  const handleHandlePointerUp = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (!canDismiss) return

    const dragStartY = dragStartYRef.current
    dragStartYRef.current = undefined

    if (dragStartY !== undefined && event.clientY - dragStartY >= CLOSE_DRAG_DISTANCE_PX) {
      onClose()
    }
  }, [canDismiss, onClose])

  const handleRenameSubmit = useCallback(async() => {
    if (!chatId || isRenameLoading || renameInFlightRef.current) return

    const trimmedName = draftName.trim()
    if (!trimmedName) {
      setRenameError(emptyNameError)
      return
    }

    const persistedTrimmedTitle = persistedTitle?.trim()
    if (persistedTrimmedTitle && trimmedName === persistedTrimmedTitle) {
      closeRenameForm()
      return
    }

    renameInFlightRef.current = true
    try {
      await renameChat({ chatId, name: trimmedName })
      setDisplayTitle(trimmedName)
      onRenameSuccess?.(trimmedName)
      onClose()
    } catch(error) {
      console.error('[TripChat] Failed to rename current trip', error)
      setRenameError(renameFailedError)
      showSnackbar(renameFailedError, 'critical')
    } finally {
      renameInFlightRef.current = false
    }
  }, [chatId, closeRenameForm, draftName, emptyNameError, isRenameLoading, onClose, onRenameSuccess, persistedTitle, renameChat, renameFailedError])

  const handleRenameSaveClick = useCallback<NonNullable<ModalFooterActionProps['onClick']>>(async(event) => {
    event.preventDefault()
    await handleRenameSubmit()
  }, [handleRenameSubmit])

  const handleRenameFormSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleRenameSubmit()
  }, [handleRenameSubmit])

  const handleRenameFormKeyDown = useCallback((event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Escape' && !isRenameLoading) {
      event.stopPropagation()
      event.preventDefault()
      closeRenameForm()
    }
  }, [closeRenameForm, isRenameLoading])

  const isActionDisabled = !chatId || isExportLoading || isRenameLoading
  const isSheetDismissDisabled = isExportLoading || isRenameLoading
  const isRenameSaveDisabled = !chatId || isRenameLoading || !draftName.trim()

  const handleDownloadPdfClick = useCallback(() => {
    if (isActionDisabled) return

    downloadPdf()
    onClose()
  }, [downloadPdf, isActionDisabled, onClose])

  const handleDownloadSpreadsheetClick = useCallback(() => {
    if (isActionDisabled) return

    downloadSpreadsheet()
    onClose()
  }, [downloadSpreadsheet, isActionDisabled, onClose])

  const handleOpenRenameFormClick = useCallback(() => {
    if (isActionDisabled) return

    openRenameForm()
  }, [isActionDisabled, openRenameForm])

  const renamePrimaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    children: saveLabel,
    disabled: isRenameSaveDisabled,
    onClick: handleRenameSaveClick,
    type: 'submit',
  }), [handleRenameSaveClick, isRenameSaveDisabled, saveLabel])

  const renameSecondaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    children: cancelLabel,
    disabled: isRenameLoading,
    onClick: closeRenameForm,
    type: 'button',
  }), [cancelLabel, closeRenameForm, isRenameLoading])

  const downloadActionIcon = useMemo(() => (
    <ActionIcon aria-hidden="true">
      <LineArrowToBottomIcon />
    </ActionIcon>
  ), [])

  const renameActionIcon = useMemo(() => (
    <ActionIcon aria-hidden="true">
      <LineEditIcon />
    </ActionIcon>
  ), [])

  if (isRenameFormOpen) {
    return (
      <ActionsSheetModal dismissible={canDismiss} height="auto" size="S">
        <RenameForm onSubmit={handleRenameFormSubmit} onKeyDown={handleRenameFormKeyDown}>
          <SheetHandleWrapper>
            <SheetHandleButton
              type="button"
              aria-label={closeSheetHandleLabel}
              disabled={!canDismiss}
              onClick={onClose}
              onPointerDown={handleHandlePointerDown}
              onPointerUp={handleHandlePointerUp}
            >
              <SheetHandle />
            </SheetHandleButton>
          </SheetHandleWrapper>
          <ModalHeader
            dismissible={canDismiss}
            title={renameTripLabel}
            onBackButtonClick={canDismiss ? closeRenameForm : undefined}
            onCloseButtonClick={canDismiss ? onClose : undefined}
          />
          <ModalBody>
            <ModalContent>
              <RenameFields>
                <TripNameField
                  label={renameInputLabel}
                  value={draftName}
                  onChange={handleRenameChange}
                  autoSelect
                  disabled={isRenameLoading}
                />
                {renameError && <MessageBanner kind="critical" density="compact">
                  {renameError}
                </MessageBanner>}
              </RenameFields>
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={renamePrimaryActionProps}
            secondaryActionProps={renameSecondaryActionProps}
          />
        </RenameForm>
      </ActionsSheetModal>
    )
  }

  return (
    <ActionsSheetModal dismissible={!isSheetDismissDisabled} height="auto" size="S">
      <SheetHandleWrapper>
        <SheetHandleButton
          type="button"
          aria-label={closeSheetHandleLabel}
          disabled={isSheetDismissDisabled}
          onClick={onClose}
          onPointerDown={handleHandlePointerDown}
          onPointerUp={handleHandlePointerUp}
        >
          <SheetHandle />
        </SheetHandleButton>
      </SheetHandleWrapper>
      <ModalHeader
        title={displayTitle}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent flushListItems>
          <ActionList>
            <ActionListItem
              type="button"
              aria-disabled={isActionDisabled}
              disabled={isActionDisabled}
              onClick={handleDownloadPdfClick}
              size="large"
              tabIndex={isActionDisabled ? -1 : undefined}
              title={downloadPdfLabel}
              startIcon={downloadActionIcon}
            />
            <ActionListItem
              type="button"
              aria-disabled={isActionDisabled}
              disabled={isActionDisabled}
              onClick={handleDownloadSpreadsheetClick}
              size="large"
              tabIndex={isActionDisabled ? -1 : undefined}
              title={downloadSpreadsheetLabel}
              startIcon={downloadActionIcon}
            />
            <ActionListItem
              type="button"
              aria-disabled={isActionDisabled}
              disabled={isActionDisabled}
              onClick={handleOpenRenameFormClick}
              size="large"
              tabIndex={isActionDisabled ? -1 : undefined}
              title={renameTripLabel}
              startIcon={renameActionIcon}
            />
          </ActionList>
        </ModalContent>
      </ModalBody>
    </ActionsSheetModal>
  )
}

export default TripChatMobileCurrentTripActionsSheet
